import { useCallback, useState } from 'react'

import { RequestLog } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { CopyButton } from '#components/CopyButton.js'
import { CopyServiceRequestButton } from '#components/CopyServiceRequestButton.js'
import {
  RequestLogFilters,
  useRequestLogFilters,
} from '#components/RequestLogFilters.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { TenderDetails } from './TenderDetails'
import { TendersList } from './TendersList'

const TendersView = () => {
  const [selectedTender, setSelectedTender] = useState<RequestLog | null>(null)
  const { filters, localFilters, setLocalFilters } = useRequestLogFilters()

  const {
    items: tenders,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<RequestLog>('request-logs', {
    type: 'tender',
    ...filters,
    limit: 50,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const handleTenderSelect = useCallback((tender: RequestLog) => {
    setSelectedTender(tender)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedTender(null)
  }, [])

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>Tender Details</span>
      {selectedTender && (
        <>
          <CopyButton
            textToCopy={`${window.location.protocol}//${window.location.host}${window.location.pathname}?requestId=${selectedTender.requestId}`}
          />
          {selectedTender.tenantRequest?.content && (
            <CopyServiceRequestButton log={selectedTender} />
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="Tenders"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No tenders found"
        filters={
          <RequestLogFilters
            isLoading={isLoading}
            onRefresh={refresh}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            errorTypes={[
              'external',
              'internal',
              'provision',
              'support',
              'whitelist',
            ]}
          />
        }
        listContent={
          tenders.length > 0 ? (
            <TendersList
              tenders={tenders}
              selectedId={selectedTender?._id}
              onSelect={handleTenderSelect}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedTender}
        onClose={handleCloseDetails}
        title={sliderTitle}
      >
        <TenderDetails tender={selectedTender} />
      </Slider>
    </>
  )
}

export { TendersView }
